import { initCategorySettings, initEnvironmentCategorySettings } from '../../actions'
import { AppAction, Dispatch, ExtraArgs, GetState } from '../../../../types'
import { ARMApplicationDetail, SettingsDeployment } from '../../../../remote-api/models'
import { SettingsApplication } from '../common/model'
import { SettingsService } from '../../util/SettingsService'
import { getAppStatus } from '../common/ResourcesList/Body/SettingsBodyResourcesListBody'
import { Environment } from '../../../../remote-api/CoreServicesRemoteApi'
import ARMRemoteApi from '../../../../remote-api/ARMRemoteApi'
import UIRemoteApi from '../../../../remote-api/UIRemoteApi'

export enum ActionType {
  FetchEnvironments = 'Monitoring/Settings/CloudHub/FetchEnvironments',
  FetchedEnvironments = 'Monitoring/Settings/CloudHub/FetchedEnvironments',
  EnvironmentSelected = 'Monitoring/Settings/CloudHub/EnvironmentSelected',
  FetchResources = 'Monitoring/Settings/CloudHub/FetchResources',
  FetchedResources = 'Monitoring/Settings/CloudHub/FetchedResources',
  FetchedCloudHubSettings = 'Monitoring/Settings/CloudHub/FetchedCloudHubSettings',
  EnableMonitoringError = 'Monitoring/Settings/CloudHub/EnableMonitoringError',
  FilteredResources = 'Monitoring/Settings/CloudHub/FilteredResources',
  ApplySuccess = 'Monitoring/Settings/CloudHub/ApplySuccess',
  ApplyError = 'Monitoring/Settings/CloudHub/ApplyError',
  ChangedCloudHubSettingsCheckbox = 'Monitoring/Settings/CloudHub/ChangedCloudHubSettingsCheckbox',
  AmplitudeSuccess = 'Monitoring/Settings/CloudHub/AmplitudeSuccess',
  AmplitudeError = 'Monitoring/Settings/CloudHub/AmplitudeError',
  ChangeQuery = 'Monitoring/Settings/CloudHub/ChangeQuery',
}

// Basic Actions

export type FetchEnvironmentsAction = {
  type: ActionType.FetchEnvironments
}

export type FetchedEnvironmentsAction = {
  type: ActionType.FetchedEnvironments
  payload: { environments: Environment[] }
}

export type FetchedCloudHubSettingsAction = {
  type: ActionType.FetchedCloudHubSettings
  payload: { settings: SettingsDeployment }
}

export type EnvironmentSelectedAction = {
  type: ActionType.EnvironmentSelected
  payload: { environmentId: string }
}

export type FetchResourcesAction = {
  type: ActionType.FetchResources
}

export type FetchedResourcesAction = {
  type: ActionType.FetchedResources
  payload: { resources: SettingsApplication[] }
}

export type FilteredResourcesAction = {
  type: ActionType.FilteredResources
  payload: { filteredResources: SettingsApplication[] }
}

export type ApplySuccessAction = {
  type: ActionType.ApplySuccess
  payload: { apply: boolean, application: SettingsApplication }
}

export type ApplyErrorAction = {
  type: ActionType.ApplyError
  payload: { apply: boolean, application: SettingsApplication }
}

export type EnableMonitoringErrorAction = {
  type: ActionType.EnableMonitoringError
  payload: { apply: boolean }
}

export type ChangedCloudHubSettingsCheckboxAction = {
  type: ActionType.ChangedCloudHubSettingsCheckbox
  payload: { value: boolean, orgId: string }
}

export type AmplitudeSuccessAction = {
  type: ActionType.AmplitudeSuccess
  payload: { apply: boolean, muleVersion: string, updateId: string, appName: string, status: string, envName: string, envType: string }
}

export type AmplitudeErrorAction = {
  type: ActionType.AmplitudeError
  payload: { apply: boolean, muleVersion: string, updateId: string, appName: string, status: string, envName: string, envType: string }
}

export type ChangeQueryAction = {
  type: ActionType.ChangeQuery
  payload: { query: string }
}

export type SettingsCloudHubActions =
  | FetchEnvironmentsAction
  | FetchedEnvironmentsAction
  | EnvironmentSelectedAction
  | FetchResourcesAction
  | FetchedResourcesAction
  | FilteredResourcesAction
  | ApplySuccessAction
  | ApplyErrorAction
  | ChangedCloudHubSettingsCheckboxAction
  | AmplitudeSuccessAction
  | AmplitudeErrorAction
  | ChangeQueryAction
  | FetchedCloudHubSettingsAction
  | EnableMonitoringErrorAction

export const fetchingEnvironmentsAction = (): AppAction => ({
  type: ActionType.FetchEnvironments
})

export const fetchedEnvironmentsAction = (environments: Environment[]): AppAction => ({
  type: ActionType.FetchedEnvironments,
  payload: {environments}
})

export const environmentSelectedAction = (environmentId: string): AppAction => ({
  type: ActionType.EnvironmentSelected,
  payload: {environmentId}
})

export const fetchResourcesAction = (): AppAction => ({
  type: ActionType.FetchResources
})

export const fetchedCloudHubSettingsAction = (settings: SettingsDeployment): AppAction => ({
  type: ActionType.FetchedCloudHubSettings,
  payload: {settings}
})

export const enableMonitoringErrorAction = (apply: boolean): AppAction => ({
  type: ActionType.EnableMonitoringError,
  payload: {apply}
})

export const fetchedResourcesAction = (resources: SettingsApplication[]): AppAction => ({
  type: ActionType.FetchedResources,
  payload: {resources}
})

export const filteredResourcesAction = (filteredResources: SettingsApplication[]): AppAction => ({
  type: ActionType.FilteredResources,
  payload: {filteredResources}
})

export const applySuccessAction = (apply: boolean, application: SettingsApplication): AppAction => ({
  type: ActionType.ApplySuccess,
  payload: {apply, application}
})

export const applyErrorAction = (apply: boolean, application: SettingsApplication): AppAction => ({
  type: ActionType.ApplyError,
  payload: {apply, application}
})

export const changedCloudHubSettingsCheckboxAction = (value: boolean, orgId: string): AppAction => ({
  type: ActionType.ChangedCloudHubSettingsCheckbox,
  payload: {value, orgId}
})

export const chAmplitudeSuccessAction =
  (apply: boolean, muleVersion: string, updateId: string, appName: string, status: string, envName: string, envType: string): AppAction => ({
  type: ActionType.AmplitudeSuccess,
  payload: {apply, muleVersion, updateId, appName, status, envName, envType}
})

export const chAmplitudeErrorAction =
  (apply: boolean, muleVersion: string, updateId: string, appName: string, status: string, envName: string, envType: string): AppAction => ({
  type: ActionType.AmplitudeError,
  payload: {apply, muleVersion, updateId, appName, status, envName, envType}
})

export const changeQueryAction = (query: string): AppAction => ({
  type: ActionType.ChangeQuery,
  payload: {query}
})

// Thunks

export const fetchEnvironments = () => (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
  const profile = getState().monitoring.auth.profile
  const organizationId = profile ? profile.organizationId : undefined

  if (organizationId) {
    dispatch(fetchingEnvironmentsAction())

    api.ui(getState)
      .fetchEnvironments(organizationId)
      .then(environments => dispatch(fetchedEnvironmentsAction(environments)))
  }
}

export const environmentSelected = (environmentId: string) => (dispatch: Dispatch) => {
  dispatch(environmentSelectedAction(environmentId))
  dispatch(fetchResources(environmentId, true))

  const enrichedCategory = dispatch(initCategorySettings('cloudhub'))
  dispatch(initEnvironmentCategorySettings(enrichedCategory, environmentId))
}

export const fetchResources = (envId: string, changedEnvironment: boolean) => (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
  if (changedEnvironment) dispatch(fetchResourcesAction())

  const remoteApi: ARMRemoteApi = api.arm(getState)
  const uiRemoteApi: UIRemoteApi = api.ui(getState)
  const settingsService: SettingsService = new SettingsService(remoteApi, uiRemoteApi)
  const profile = getState().monitoring.auth.profile
  const orgId = profile ? profile.organizationId : undefined

  if (orgId) {
    settingsService
      .fetchApplications(orgId, envId)
      .then(response => {
        return settingsService.fetchSettingsApplications(orgId, envId, response)
          .then(appDetails => {
            const validApps: SettingsApplication[] =
              settingsService.validateMuleVersionApps(appDetails)
                .sort((a, b) => {
                  return a.name > b.name ? 1 : a.name === b.name ? 0 : -1
                })

            const selectedEnv = getState().monitoring.cloudHubSettings.environmentSelector.selected
            if (selectedEnv && selectedEnv.id === envId) dispatch(fetchedResourcesAction(validApps))

            const query = getState().monitoring.cloudHubSettings.query
            if (query) dispatch(filterResources(query))
          })
      })
      .catch(() => dispatch(fetchedResourcesAction([])))
  }
}

export const fetchCloudHubSettings = (enable: boolean) => (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
  const remoteApi: ARMRemoteApi = api.arm(getState)
  const uiRemoteApi: UIRemoteApi = api.ui(getState)
  const settingsService: SettingsService = new SettingsService(remoteApi, uiRemoteApi)
  const profile = getState().monitoring.auth.profile
  const orgId = profile ? profile.organizationId : undefined

  if (orgId) {
    settingsService
      .fetchCloudHubSettings(orgId)
      .then(response => {
        dispatch(fetchedCloudHubSettingsAction({...response.settings, isLoading: false, isChanging: false}))
      })
      .catch((error) => {
        if (error.errorCode === 404 || error.errorCode === 0) {
          dispatch(postCloudHubSettings(enable))
        } else {
          dispatch(enableMonitoringErrorAction(enable))
        }
      })
  }
}

export const postCloudHubSettings = (enable: boolean) => (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
  const lastDeploymentState = getState().monitoring.cloudHubSettings.cloudHubDeployment
  dispatch(fetchedCloudHubSettingsAction({...lastDeploymentState, isLoading: false, isChanging: true}))

  const remoteApi: ARMRemoteApi = api.arm(getState)
  const uiRemoteApi: UIRemoteApi = api.ui(getState)
  const settingsService: SettingsService = new SettingsService(remoteApi, uiRemoteApi)
  const profile = getState().monitoring.auth.profile
  const orgId = profile ? profile.organizationId : undefined

  if (orgId) {
    settingsService
      .postCloudHubSettings(orgId, enable)
      .then(response => {
        dispatch(changedCloudHubSettingsCheckboxAction(enable, orgId))
        dispatch(fetchedCloudHubSettingsAction({...response.settings, isLoading: false, isChanging: false}))
      })
      .catch(() => {
        dispatch(fetchedCloudHubSettingsAction({...lastDeploymentState, isLoading: false, isChanging: false}))
        dispatch(enableMonitoringErrorAction(!!lastDeploymentState.deployment.newAppsAgentEnabled))
      })
  }
}

export const filterResources = (input: string) => (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
  dispatch(changeQueryAction(input))

  const remoteApi: ARMRemoteApi = api.arm(getState)
  const uiRemoteApi: UIRemoteApi = api.ui(getState)
  const settingsService: SettingsService = new SettingsService(remoteApi, uiRemoteApi)
  const apps: SettingsApplication[] | undefined = getState().monitoring.cloudHubSettings.resourceList.resources
  const filteredResources = settingsService.filterApplicationsByName(input, apps);
  dispatch(filteredResourcesAction(filteredResources))
}

export const setAMEnabled = (enabled: boolean, app: SettingsApplication) => (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
  dispatch(fetchResourcesAction())

  const remoteApi: ARMRemoteApi = api.arm(getState)
  const uiRemoteApi: UIRemoteApi = api.ui(getState)
  const settingsService: SettingsService = new SettingsService(remoteApi, uiRemoteApi)
  const orgId = getState().monitoring.auth.profile.organizationId
  const env = getState().monitoring.cloudHubSettings.environmentSelector.selected

  if (env) {
    const appStatus = getAppStatus(app.status, app.deploymentUpdateStatus)
    settingsService
      .setApplicationAMEnabled(orgId, app, !enabled, env ? env.id : env)
      .then((response: ARMApplicationDetail) => {
        dispatch(fetchResources(env ? env.id : env, true))
        dispatch(applySuccessAction(enabled, app))
        if (app.muleVersion)
          dispatch(chAmplitudeSuccessAction(!enabled, app.muleVersion.version, app.muleVersion.updateId, app.name, appStatus, env.name, env.type))
      })
      .catch(() => {
        dispatch(applyErrorAction(enabled, app))
        if (app.muleVersion)
          dispatch(chAmplitudeErrorAction(!enabled, app.muleVersion.version, app.muleVersion.updateId, app.name, appStatus, env.name, env.type))
      })
  }
}

export const checkedMonitoring = (id: string) =>  (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
  const apps: SettingsApplication[] | undefined = getState().monitoring.cloudHubSettings.resourceList.resources
  if (apps) {
    const appIndex = apps.findIndex((app => app.name === id))
    const updatedApp = { ...apps[appIndex], monitoringChecked: !apps[appIndex].monitoringChecked};
    const updatedApps = [
      ...apps.slice(0, appIndex),
      updatedApp,
      ...apps.slice(appIndex + 1),
    ];
    dispatch(fetchedResourcesAction(updatedApps))
  }
}
